import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Loader2, ChefHat, Star, Tractor, HelpCircle, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/authContext";
import { useLocation } from "@/lib/locationContext";
import { useAppContext } from "@/lib/onboardingContext";
import { getCurrencySymbol } from "@/lib/currency";
import { toast } from "sonner";

const BRAND_GREEN = "#2d8653";
const BRAND_ORANGE = "#e76f00";
const BRAND_DARK = "#1a1a1a";

const CATEGORIES = ["Starter", "Main", "Side", "Dessert", "Drink", "Other"] as const;
const POPULARITIES = [
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
] as const;

type Popularity = typeof POPULARITIES[number]["value"];
type Quadrant = "Star" | "Plow Horse" | "Puzzle" | "Dog";

interface MenuItemRow {
  id: string;
  name: string;
  category: string;
  price: number;
  food_cost_pct: number;
  popularity: Popularity;
}

/** Classic menu engineering matrix:
 *  - high popularity & high margin → Star
 *  - high popularity & low margin  → Plow Horse
 *  - low popularity  & high margin → Puzzle
 *  - low popularity  & low margin  → Dog
 *  Medium popularity is treated as high-popularity for star/plow split
 *  to avoid hiding decent sellers. Margin threshold = 65% (food cost ≤ 35%). */
function classify(item: MenuItemRow): Quadrant {
  const margin = 100 - Number(item.food_cost_pct ?? 0);
  const highMargin = margin >= 65;
  const popular = item.popularity === "high" || item.popularity === "medium";
  if (popular && highMargin) return "Star";
  if (popular && !highMargin) return "Plow Horse";
  if (!popular && highMargin) return "Puzzle";
  return "Dog";
}

const QUADRANTS: { key: Quadrant; icon: any; color: string; description: string }[] = [
  { key: "Star",       icon: Star,          color: "#2d8653", description: "Popular & profitable — protect and feature" },
  { key: "Plow Horse", icon: Tractor,       color: "#e76f00", description: "Popular but low margin — re-engineer cost" },
  { key: "Puzzle",     icon: HelpCircle,    color: "#5b6cf2", description: "High margin, low sales — push or rename" },
  { key: "Dog",        icon: AlertTriangle, color: "#c0392b", description: "Slow & low margin — cut or rework" },
];

export default function MenuEngineering() {
  const { user } = useAuth();
  const { activeLocation } = useLocation();
  const { onboarding } = useAppContext();
  const cs = getCurrencySymbol(onboarding.country || onboarding.city || "");

  const [items, setItems] = useState<MenuItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [category, setCategory] = useState<string>("Main");
  const [price, setPrice] = useState("");
  const [foodCost, setFoodCost] = useState("");
  const [popularity, setPopularity] = useState<Popularity>("medium");

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      let q = supabase
        .from("menu_items")
        .select("id, name, category, price, food_cost_pct, popularity")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (activeLocation?.id) q = q.eq("location_id", activeLocation.id);
      const { data, error } = await q;
      if (cancelled) return;
      if (error) toast.error("Couldn't load menu items");
      else setItems((data ?? []) as MenuItemRow[]);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user, activeLocation?.id]);

  const grouped = useMemo(() => {
    const out: Record<Quadrant, MenuItemRow[]> = {
      "Star": [], "Plow Horse": [], "Puzzle": [], "Dog": [],
    };
    items.forEach(i => out[classify(i)].push(i));
    return out;
  }, [items]);

  const handleSave = async () => {
    if (!user) return;
    if (!name.trim()) { toast.error("Add a dish name"); return; }
    const p = Number(price);
    const fc = Number(foodCost);
    if (p < 0 || fc < 0 || fc > 100) {
      toast.error("Price ≥ 0 and food cost % between 0 and 100");
      return;
    }
    setSaving(true);
    const { data, error } = await supabase
      .from("menu_items")
      .insert({
        user_id: user.id,
        location_id: activeLocation?.id ?? null,
        name: name.trim(),
        category,
        price: p,
        food_cost_pct: fc,
        popularity,
      })
      .select("id, name, category, price, food_cost_pct, popularity")
      .single();
    setSaving(false);
    if (error) { toast.error("Couldn't save dish"); return; }
    setItems(prev => [data as MenuItemRow, ...prev]);
    setName(""); setPrice(""); setFoodCost("");
    toast.success("Dish added");
  };

  const handleDelete = async (id: string) => {
    const prev = items;
    setItems(items.filter(i => i.id !== id));
    const { error } = await supabase.from("menu_items").delete().eq("id", id);
    if (error) {
      setItems(prev);
      toast.error("Couldn't delete");
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-4 sm:p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-5">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${BRAND_GREEN}1a` }}
        >
          <ChefHat className="w-4 h-4" style={{ color: BRAND_GREEN }} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-heading text-lg font-medium" style={{ color: BRAND_DARK }}>
            Menu Engineering
          </h2>
          <p className="text-xs text-muted-foreground">
            Auto-classifies dishes as Star, Plow Horse, Puzzle, or Dog
          </p>
        </div>
      </div>

      {/* Add form */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
        <div className="col-span-2">
          <Label className="text-xs">Dish name</Label>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Truffle pasta" />
        </div>
        <div>
          <Label className="text-xs">Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Price ({cs})</Label>
          <Input type="number" min="0" step="0.01" value={price} onChange={e => setPrice(e.target.value)} placeholder="0" />
        </div>
        <div>
          <Label className="text-xs">Food cost %</Label>
          <Input type="number" min="0" max="100" step="0.1" value={foodCost} onChange={e => setFoodCost(e.target.value)} placeholder="30" />
        </div>
        <div>
          <Label className="text-xs">Popularity</Label>
          <Select value={popularity} onValueChange={(v) => setPopularity(v as Popularity)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {POPULARITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2 lg:col-span-6 flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="gap-2"
            style={{ backgroundColor: BRAND_GREEN, color: "white" }}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add dish
          </Button>
        </div>
      </div>

      {/* 2x2 grid */}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading menu…
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground italic py-6">
          Add your first dish above to see the engineering matrix.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {QUADRANTS.map(({ key, icon: Icon, color, description }) => {
            const list = grouped[key];
            return (
              <div key={key} className="rounded-xl border border-border bg-background p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="w-4 h-4" style={{ color }} />
                  <span className="font-medium text-sm" style={{ color: BRAND_DARK }}>{key}</span>
                  <span className="ml-auto text-xs tabular-nums text-muted-foreground">{list.length}</span>
                </div>
                <p className="text-xs text-muted-foreground mb-3">{description}</p>
                {list.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">None yet</p>
                ) : (
                  <ul className="space-y-1.5">
                    {list.map(item => {
                      const margin = 100 - Number(item.food_cost_pct);
                      return (
                        <li
                          key={item.id}
                          className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-muted/40"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="font-medium truncate" style={{ color: BRAND_DARK }}>
                              {item.name}
                            </div>
                            <div className="text-xs text-muted-foreground tabular-nums">
                              {cs}{Number(item.price).toFixed(2)} · {margin.toFixed(0)}% margin
                            </div>
                          </div>
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => handleDelete(item.id)}
                            aria-label={`Delete ${item.name}`}
                            className="shrink-0"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}